import { supabase } from '@/integrations/supabase/client';
import { initUTMTracking } from '@/lib/utm';

interface AnalyticsEventData {
  event_type: string;
  email?: string;
  source?: string;
  metadata?: Record<string, any>;
}

interface AnalyticsConfig {
  enabled: boolean;
  debug: boolean;
  fallbackToConsole: boolean;
}

class AnalyticsService {
  private config: AnalyticsConfig = {
    enabled: true,
    debug: false,
    fallbackToConsole: typeof window !== 'undefined' && window.location.hostname === 'localhost',
  };

  constructor() {
    // Auto-detect if analytics should be enabled
    if (typeof window !== 'undefined') {
      this.config.debug = window.location.hostname === 'localhost' || window.location.hostname.includes('sandbox');
    }
  }

  /**
   * Track an analytics event
   */
  async track(event_type: string, data: Omit<AnalyticsEventData, 'event_type'> = {}) {
    if (!this.config.enabled) return;

    const eventData: AnalyticsEventData = {
      event_type,
      source: data.source || 'website',
      email: data.email,
      metadata: {
        ...initUTMTracking(),
        timestamp: new Date().toISOString(),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        ...data.metadata,
      },
    };

    // Always log to console in debug mode, but sanitize PII in production
    if (this.config.debug || this.config.fallbackToConsole) {
      const sanitizedEventData = this.config.debug ? eventData : this.sanitizeEventData(eventData);
      console.log('📊 Analytics Event:', sanitizedEventData);
    }

    try {
      // Send to Supabase analytics
      const { error } = await supabase.functions.invoke('analytics-track', {
        body: eventData
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.warn('Analytics tracking failed:', error);
      // Only log to console in development/debug mode to prevent PII exposure
      if (this.config.fallbackToConsole && this.config.debug) {
        console.info('📊 Analytics Fallback:', eventData);
      }
    }
  }

  /**
   * Sanitize event data to remove PII from console logs
   */
  private sanitizeEventData(eventData: AnalyticsEventData) {
    const sanitized = { ...eventData };
    
    // Remove or mask email addresses
    if (sanitized.email) {
      sanitized.email = '***@***.***';
    }
    
    // Remove or mask sensitive metadata
    if (sanitized.metadata) {
      const safeMeta = { ...sanitized.metadata };
      if (safeMeta.user_agent) {
        safeMeta.user_agent = 'USER_AGENT_HIDDEN';
      }
      sanitized.metadata = safeMeta;
    }
    
    return sanitized;
  }

  /**
   * Track page view with route transition
   */
  trackPageView(route: string) {
    this.track('page_view', {
      metadata: { route }
    });
  }

  /**
   * Track waitlist events
   */
  trackWaitlistView(source: string = 'website') {
    this.track('waitlist_view', { source });
  }

  trackWaitlistSubmit(email: string, source: string = 'website') {
    this.track('waitlist_submit', { email, source });
  }

  /**
   * Track pre-qualification events
   */
  trackPrequalStart(metadata?: Record<string, any>) {
    this.track('prequal_start', { metadata });
  }

  trackPrequalComplete(amount: number, category: string, email?: string, metadata?: Record<string, any>) {
    this.track('prequal_complete', {
      email,
      metadata: {
        amount,
        category,
        ...metadata
      }
    });
  }

  /**
   * Track partner events
   */
  trackPartnerView() {
    this.track('partner_view');
  }

  trackPartnerSubmit(email: string, companyName: string, vertical: string) {
    this.track('partner_submit', {
      email,
      source: 'partner_form',
      metadata: {
        company_name: companyName,
        vertical
      }
    });
  }

  /**
   * Track calculator usage
   */
  trackCalcUse(amount: number, months: number, monthlyPayment: number) {
    this.track('calc_use', {
      metadata: {
        amount,
        months,
        monthly_payment: monthlyPayment
      }
    });
  }

  /**
   * Configure analytics service
   */
  configure(config: Partial<AnalyticsConfig>) {
    this.config = { ...this.config, ...config };
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// Simple track function for quick metrics (investor-friendly)
export function track(event: string, props?: Record<string, any>) {
  // TODO: wire to PostHog/Segment/GA. For now, log.
  if (process.env.NODE_ENV !== "production") {
    console.debug("[track]", event, props);
  }
  
  // Also send through our analytics service
  analytics.track(event, { metadata: props });
}

// Export individual tracking functions for convenience with proper binding
export const trackEvent = (event_type: string, data?: Omit<AnalyticsEventData, 'event_type'>) => analytics.track(event_type, data);
export const trackPageView = (route: string) => analytics.trackPageView(route);
export const trackWaitlistView = (source?: string) => analytics.trackWaitlistView(source);
export const trackWaitlistSubmit = (email: string, source?: string) => analytics.trackWaitlistSubmit(email, source);
export const trackPrequalStart = (metadata?: Record<string, any>) => analytics.trackPrequalStart(metadata);
export const trackPrequalComplete = (amount: number, category: string, email?: string, metadata?: Record<string, any>) => 
  analytics.trackPrequalComplete(amount, category, email, metadata);
export const trackPartnerView = () => analytics.trackPartnerView();
export const trackPartnerSubmit = (email: string, companyName: string, vertical: string) => 
  analytics.trackPartnerSubmit(email, companyName, vertical);
export const trackCalcUse = (amount: number, months: number, monthlyPayment: number) => 
  analytics.trackCalcUse(amount, months, monthlyPayment);
export const configureAnalytics = (config: Partial<AnalyticsConfig>) => analytics.configure(config);